import React, { useState } from "react";
import {
  getAuth,
  signInWithEmailAndPassword,
  RecaptchaVerifier,
  signInWithPhoneNumber,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { db } from "../config/firebaseConfig";
import { getDashboardConfig } from "../utils/roleAccess";
import { Eye, EyeOff } from "lucide-react";

function Login() {
  const [tab, setTab] = useState("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const navigate = useNavigate();

  const redirectUserToRoleDashboard = async (firebaseUser) => {
    if (!firebaseUser?.uid) {
      navigate("/");
      return;
    }

    try {
      const userRef = doc(db, "users", firebaseUser.uid);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        navigate("/");
        return;
      }

      const dashboardConfig = getDashboardConfig(userSnap.data());
      navigate(dashboardConfig?.path || "/");
    } catch (error) {
      console.error("Failed to resolve dashboard route:", error);
      navigate("/");
    }
  };

  // Email login
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMsg("");
    const auth = getAuth();
    try {
      const credential = await signInWithEmailAndPassword(
        auth,
        email,
        password,
      );
      setMsg("Login successful!");
      await redirectUserToRoleDashboard(credential.user);
    } catch (err) {
      setMsg(err.message);
    }
    setLoading(false);
  };

  // Phone login
  const handlePhoneLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMsg("");
    const auth = getAuth();
    try {
      window.recaptchaVerifier = new RecaptchaVerifier(
        "recaptcha-container",
        { size: "invisible" },
        auth,
      );
      const confirmationResult = await signInWithPhoneNumber(
        auth,
        phone,
        window.recaptchaVerifier,
      );
      window.confirmationResult = confirmationResult;
      setOtpSent(true);
      setMsg("OTP sent to your phone.");
    } catch (err) {
      setMsg(err.message);
    }
    setLoading(false);
  };

  // Verify OTP
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMsg("");
    try {
      const result = await window.confirmationResult.confirm(otp);
      setMsg("Login successful!");
      await redirectUserToRoleDashboard(result.user);
    } catch (err) {
      setMsg(err.message);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-100 to-blue-300">
      <div className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-md">
        <h2 className="text-2xl font-bold text-center text-blue-700 mb-6">
          Login
        </h2>
        <div className="flex justify-center mb-6">
          <button
            className={`px-4 py-2 rounded-l-lg ${
              tab === "email"
                ? "bg-blue-600 text-white"
                : "bg-blue-100 text-blue-700"
            }`}
            onClick={() => setTab("email")}
          >
            Email
          </button>
          <button
            className={`px-4 py-2 rounded-r-lg ${
              tab === "phone"
                ? "bg-blue-600 text-white"
                : "bg-blue-100 text-blue-700"
            }`}
            onClick={() => setTab("phone")}
          >
            Phone
          </button>
        </div>
        {tab === "email" && (
          <form onSubmit={handleEmailLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold mb-1">Email</label>
              <input
                type="email"
                className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="username"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  className="w-full border rounded px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((visible) => !visible)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-blue-600"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  title={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              className="w-full bg-blue-600 text-white py-2 rounded font-semibold hover:bg-blue-700 transition"
              disabled={loading}
            >
              {loading ? "Logging in..." : "Login"}
            </button>
          </form>
        )}
        {tab === "phone" && (
          <form
            onSubmit={otpSent ? handleVerifyOtp : handlePhoneLogin}
            className="space-y-4"
          >
            {!otpSent && (
              <div>
                <label className="block text-sm font-semibold mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+91XXXXXXXXXX"
                  required
                />
              </div>
            )}
            {otpSent && (
              <div>
                <label className="block text-sm font-semibold mb-1">OTP</label>
                <input
                  type="text"
                  className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  required
                />
              </div>
            )}
            <div id="recaptcha-container"></div>
            <button
              type="submit"
              className="w-full bg-blue-600 text-white py-2 rounded font-semibold hover:bg-blue-700 transition"
              disabled={loading}
            >
              {loading
                ? otpSent
                  ? "Verifying..."
                  : "Sending OTP..."
                : otpSent
                  ? "Verify OTP"
                  : "Send OTP"}
            </button>
          </form>
        )}
        {msg && (
          <div className="mt-4 text-center text-sm text-red-600">{msg}</div>
        )}
      </div>
    </div>
  );
}

export default Login;
